import { FileText, ExternalLink } from "lucide-react";
import { fileUrl } from "@/lib/api";
import { OUTCOME_LABEL, MEASURE_LABEL, DETAIL_LABEL, fmtDateTime, fmtEuro } from "@/lib/format";
import { Field } from "@/components/Common";

export const AttachmentGrid = ({ attachments = [], testId = "attachments-grid" }) => {
  if (!attachments.length) return <div className="text-sm text-muted-foreground" data-testid={`${testId}-empty`}>Nessun allegato</div>;
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid={testId}>
      {attachments.map((a) => (
        <a key={a.id} href={fileUrl(a.id)} target="_blank" rel="noreferrer" className="group block border bg-white transition-colors hover:border-primary" data-testid={`attachment-${a.id}`}>
          {a.mime?.startsWith("image/") ? (
            <img src={fileUrl(a.id)} alt={a.filename} className="aspect-square w-full object-cover" loading="lazy" />
          ) : (
            <div className="flex aspect-square items-center justify-center bg-muted"><FileText className="h-8 w-8 text-muted-foreground" /></div>
          )}
          <div className="flex items-center gap-1 border-t px-2 py-1.5 text-xs">
            <span className="truncate">{a.kind} · {a.filename}</span>
            <ExternalLink className="ml-auto h-3 w-3 shrink-0 opacity-0 group-hover:opacity-100" />
          </div>
          <div className="px-2 pb-1.5 font-mono text-[10px] uppercase text-muted-foreground">{a.storage === "drive" ? "Google Drive" : a.storage === "object" ? "Archivio documenti" : "Archivio locale"}</div>
        </a>
      ))}
    </div>
  );
};

const KV = ({ title, obj, labels }) => {
  const entries = Object.entries(obj || {}).filter(([, v]) => v);
  if (!entries.length) return null;
  return (
    <div>
      <div className="micro-label mb-2">{title}</div>
      <div className="grid grid-cols-2 gap-3">{entries.map(([k, v]) => <Field key={k} label={labels[k] || k} value={v} mono />)}</div>
    </div>
  );
};

const REPORT_STATUS = { SUBMITTED: "Inviato, da validare", APPROVED: "Approvato", REJECTED: "Respinto", INFO: "Sospensione (non valorizzabile)" };

export const ReportView = ({ report }) => (
  <div className="space-y-5" data-testid={`report-${report.id}`}>
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
      <Field label="Tentativo" value={`#${report.attempt_no}`} mono />
      <Field label="Tecnico" value={report.technician_name} />
      <Field label="Esito" value={OUTCOME_LABEL[report.outcome]} />
      <Field label="Causale" value={report.reason_code} mono />
      <Field label="Arrivo" value={fmtDateTime(report.arrived_at)} />
      <Field label="Partenza" value={fmtDateTime(report.departed_at)} />
      <Field label="Stato rapportino" value={REPORT_STATUS[report.status]} />
      {report.restoration_minutes != null && <Field label="Tempo ripristino" value={`${Math.floor(report.restoration_minutes / 60)}h ${report.restoration_minutes % 60}m`} mono />}
      {report.sla_met != null && <Field label="SLA" value={report.sla_met ? "Rispettato" : "Non rispettato"} />}
    </div>
    <KV title="Misure" obj={report.measures} labels={MEASURE_LABEL} />
    <KV title="Dettagli tecnici" obj={report.details} labels={DETAIL_LABEL} />
    {report.services?.length > 0 && (
      <div data-testid="report-services">
        <div className="micro-label mb-2">Prestazioni dichiarate dal tecnico</div>
        <ul className="divide-y border text-sm">
          {report.services.map((s) => <li key={s.code} className="flex justify-between px-3 py-2"><span><span className="mr-2 font-mono text-xs">{s.code}</span>{s.description}</span><span className="font-mono">× {s.qty}</span></li>)}
        </ul>
      </div>
    )}
    {report.materials?.length > 0 && (
      <div>
        <div className="micro-label mb-2">Materiali</div>
        <ul className="divide-y border text-sm">
          {report.materials.map((m, i) => (
            <li key={i} className="px-3 py-2">
              <div className="flex justify-between"><span>{m.item_code && <span className="mr-2 font-mono text-xs">{m.item_code}</span>}{m.name}{m.serial && <span className="ml-2 font-mono text-xs text-muted-foreground">S/N {m.serial}</span>}</span><span className="font-mono">× {m.qty}{m.unit ? ` ${m.unit}` : ""}</span></div>
              {m.warnings?.length > 0 && <div className="mt-1 text-xs text-amber-700" data-testid={`report-material-warning-${i}`}>{m.warnings.join(" · ")}</div>}
            </li>
          ))}
        </ul>
      </div>
    )}
    {report.returns?.length > 0 && (
      <div data-testid="report-returns">
        <div className="micro-label mb-2">Apparati rientrati</div>
        <ul className="divide-y border text-sm">
          {report.returns.map((r, i) => (
            <li key={i} className="px-3 py-2">
              <span className="mr-2 font-mono text-xs">{r.item_code}</span>{r.name}<span className="ml-2 font-mono text-xs text-muted-foreground">S/N {r.serial}</span>
              <span className="ml-2 text-xs font-semibold">{{ GUASTO: "Guasto", RECUPERATO: "Recuperato", SOSTITUITO: "Sostituito" }[r.reason]}</span>
              {r.notes && <div className="text-xs text-muted-foreground">{r.notes}</div>}
              {r.warnings?.length > 0 && <div className="text-xs text-amber-700">{r.warnings.join(" · ")}</div>}
            </li>
          ))}
        </ul>
      </div>
    )}
    {report.notes && <Field label="Note tecnico" value={report.notes} />}
    {report.validation && (
      <div className="border-l-2 border-primary bg-accent/40 p-4 text-sm">
        <div className="font-semibold">Validazione: {report.validation.by} · {fmtDateTime(report.validation.at)}{report.validation.price_list && ` · ${report.validation.price_list}`}</div>
        {report.validation.note && <div className="mt-1 text-muted-foreground">{report.validation.note}</div>}
        {report.validation.lines?.map((l) => <div key={l.price_code} className="mt-1 font-mono text-xs">{l.price_code} {l.description} × {l.qty} = {fmtEuro(l.amount)}</div>)}
        {report.validation.total != null && <div className="mt-2 font-mono font-semibold">Totale {fmtEuro(report.validation.total)}</div>}
      </div>
    )}
  </div>
);
